import { useEffect, useMemo, useRef, useState } from "react";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import TimeSeriesGraph from "../timeSeriesGraph/index";
import { useTranslation } from "react-i18next";
import { progressStepAtom } from "../../utils/atoms/progressLoadingAtom";
import { useAtom } from "jotai";
import {
  FileInput,
  Select,
  Button,
  Grid,
  useMantineColorScheme,
  Switch,
  TextInput
} from "@mantine/core";
import {
  IconCalendar,
  IconCaretDownFilled,
  IconFileSearch,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import { helpAtom } from "../../utils/atoms/helpAtom";
import loadJson from "../../components/help/helpCardMenu.json";
import { generateTimeSeries, useProjectDateFormat } from "../../utils/helpers";
import "./index.scss";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { sideMenu } from "../../utils/atoms/sideMenuAtom";
import _ from "lodash";
import { formatInputValue, responsiveCol, updatedUnits, useAssessmentRouteParams, useCurrentDesignComponentLists } from "../../constants/helper";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import ProjectNumberInput from "../common/ProjectNumberInput";

const LoadAttributesForm = ({
  handleFileInputChange,
  startDate,
  updateLoadState,
  dataAssetElectric,
  dataAssetThermal,
  handleFileClick,
  form,
  loadParameters,
  handleFileDownload,
}) => {
  const { t } = useTranslation();
  const { designId, designVersionId } = useAssessmentRouteParams();
  const isThermal =
    loadParameters?.componentAttributes?.loadParameters?.profileDetail
      ?.useThermalLoad;
  const intervalTime =
    loadParameters?.componentAttributes?.loadParameters?.profileDetail
      ?.timeResolution;
  const timeSeriesUnit =updatedUnits?.includes(loadParameters?.componentAttributes?.loadParameters?.profileDetail?.units)?"kW":loadParameters?.componentAttributes?.loadParameters?.profileDetail?.units;

  // Memoize props for TimeSeriesGraph to prevent unnecessary re-renders
  const memoDataAsssetElectric = useMemo(
    () => dataAssetElectric,
    [dataAssetElectric]
  );
  const memoDataAsssetThermal = useMemo(
    () => (isThermal ? dataAssetThermal : []),
    [isThermal, dataAssetThermal]
  );
  const memoName = useMemo(
    () =>
      isThermal && dataAssetThermal?.length > 0 ? "Thermal" : "Electrical",
    [isThermal, dataAssetThermal]
  );
  const memoChartStartDate = useMemo(() => startDate, [startDate]);
  const memoInterval = useMemo(() => intervalTime, [intervalTime]);
  const memoYAxisName = useMemo(() => timeSeriesUnit, [timeSeriesUnit]);
  const [loading] = useAtom(progressStepAtom);
  const timeInputRef = useRef();
  const graphRef = useRef();
  const hasSyncedThermalRef = useRef(false);
  const hasValidatedThermalRef = useRef(false);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const projectDateFormat = useProjectDateFormat();
  const [helpMenuData] = useAtom(helpContextMenuAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);
  const isAshraePresent = loadParameters?.datasource?.toLowerCase() === "ashrae";

  const { colorScheme } = useMantineColorScheme();

  const currentDate = new Date();
  const reducedDate = new Date(currentDate);

  const isPowerFactorDisplayed=['VA', 'kVA', 'VAh', 'kVAh']?.includes(loadParameters?.componentAttributes?.loadParameters?.profileDetail?.units)

  reducedDate.setDate(currentDate.getDate() - 1);
  reducedDate.setFullYear(currentDate.getFullYear() - 1);

  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  );  

  const isElectricalDataPresent = _.find(selectedDesignAndDesignVersion?.componentDTOList, {
    componentType: "ElectricalLoad",
  })?.componentType;

  // Get ThermalLoad component data to populate thermalEnergyPrice and thermalEmissionFactor
  const thermalLoadComponent = useMemo(
    () => _.find(selectedDesignAndDesignVersion?.componentDTOList, {
      componentType: "ThermalLoad",
    }),
    [selectedDesignAndDesignVersion?.componentDTOList]
  );

  const thermalLoadAttributes = useMemo(
    () => {
      if (!thermalLoadComponent) {
        return {
          thermalEnergyPrice: 0,
          thermalEmissionFactor: 0,
        };
      }
      return {
        thermalEnergyPrice: thermalLoadComponent?.componentAttributes?.loadParameters?.profileDetail?.ThermalEnergyPrice,
        thermalEmissionFactor: thermalLoadComponent?.componentAttributes?.loadParameters?.profileDetail?.ThermalEmissionFactor,
      };
    },
    [thermalLoadComponent]
  );

  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const [collapseSideMenu] = useAtom(collapseAtom);
  const [sideMenuShow] = useAtom(sideMenu);
  const containerRef = useRef(null);

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.load?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };

  // Delayed chart mount (simple 2s after data present)
  const [chartDelayReady, setChartDelayReady] = useState(false);
  const delayTimerRef = useRef(null);
  useEffect(() => {
    const hasData =
      (!isThermal && dataAssetElectric?.length > 0) ||
      (isThermal && dataAssetElectric?.length > 0) ||
      (isThermal && dataAssetThermal?.length > 0);
    if (!hasData) {
      // reset if data removed
      setChartDelayReady(false);
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      return;
    }
    if (chartDelayReady) return; // already ready
    if (delayTimerRef.current) return; // already scheduled
    delayTimerRef.current = setTimeout(() => {
      setChartDelayReady(true);
      delayTimerRef.current = null;
    }, 500); // 2 seconds delay
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };
  }, [isThermal, dataAssetElectric, dataAssetThermal, chartDelayReady]);
  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  // Auto-populate thermal fields from ThermalLoad component response
  useEffect(() => {
    if (isThermal && !hasSyncedThermalRef.current && !isAshraePresent) {
      hasSyncedThermalRef.current = true;
      // Always sync with thermalLoadAttributes (0 if no component, or values from component)
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "ThermalEnergyPrice",
        thermalLoadAttributes.thermalEnergyPrice
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "ThermalEmissionFactor",
        thermalLoadAttributes.thermalEmissionFactor
      );
    } else if (!isThermal && !isAshraePresent) {
      // Reset ref when disabling thermal so next enable will re-populate
      hasSyncedThermalRef.current = false;
    }
  }, [isThermal, thermalLoadAttributes, updateLoadState]);

  useEffect(() => {
    if (dataAssetThermal?.length > 0 || dataAssetElectric?.length > 0) {
      graphRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [dataAssetThermal, dataAssetElectric, designVersionId]);

  // Validate form when thermal load is enabled and component is present
  useEffect(() => {
    if (isThermal && thermalLoadComponent && !hasValidatedThermalRef.current) {
      hasValidatedThermalRef.current = true;
      form.validate();
    } else if (!isThermal || !thermalLoadComponent) {
      hasValidatedThermalRef.current = false;
    }
  }, [isThermal, thermalLoadComponent]);

  return (
    <div ref={containerRef}>
      <span
        className="mb-3 mt-2"
        style={{ fontSize: "17px", color: "#eb7857" }}
      >
        <b>{t("common.note")}</b>&nbsp;<br/>
        1. {t("loadAttributes.note.thermalUpload")}
        <br/>
        2. {t("loadAttributes.note.thermalUpload2")}
      </span>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <DateInput
            key={`load-start-date-${projectDateFormat}`}
            {...getFieldAttributes("field-1")}
            data-id="field-1"
            label={t("startDate")}
            clearable
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.startDate"
            )}
            onChange={(date) => {
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "startDate",
                date
              );
            }}
            placeholder={t("startDate")}
            valueFormat={projectDateFormat}
            required
            rightSection={
              <div style={grayIconStyle}>
                <IconCalendar size="1.1rem" stroke={1.5} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            popoverProps={{
              withinPortal: true,
              position: "bottom-start",
            }}
            maxDate={reducedDate}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-2")}
            data-id="field-2"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("startTime")}
            data={generateTimeSeries()}
            value={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.startTime
            }
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.startTime"
            )}
            onChange={(value) => {
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "startTime",
                value
              );
            }}
            required
            ref={timeInputRef}
            size="sm"
            withinPortal={true}
            // dropdownPosition="top"
            styles={{
              dropdown: {
                left: 0,
                width: "100px",
                maxWidth: "100px",
              },
            }}
            searchable
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-3")}
            data-id="field-3"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("timeResolution")}
            data={[
              { value: 15, label: "15" },
              { value: 30, label: "30" },
              { value: 60, label: "60" },
            ]}
            value={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.timeResolution
            }
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.timeResolution"
            )}
            onChange={(value) => {
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "timeResolution",
                value
              );
            }}
            required
            withinPortal
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-4")}
            data-id="field-4"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("pv.units")}
            data={[
              { value: "kW", label: "kW" },
              { value: "W", label: "W" },
              { value: "kWh", label: "kWh" },
              { value: "Wh", label: "Wh" },
              { value: "VA", label: "VA" },
              { value: "kVA", label: "kVA" },
              { value: "VAh", label: "VAh" },
              { value: "kVAh", label: "kVAh" },
            ]}
            disabled={loadParameters?.componentAttributes?.loadParameters?.profileDetail?.useThermalLoad}
            value={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.units
            }
            required
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.units"
            )}
            onChange={(value) => {
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "units",
                value
              );
            }}
            withinPortal
          />
        </Grid.Col>
      </Grid>
      {isPowerFactorDisplayed && <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <TextInput
            onClick={e => e.stopPropagation()}
            {...getFieldAttributes("field-10")}
            data-id="field-10"
            label={
              <span>
                {t("loadAttributes.powerFactor")}
                <span style={{ fontSize: '11px', color: '#888', marginTop: 2 }}>
                  &nbsp;{t("loadAttributes.powerFactorHint")}
                </span>
              </span>
              }
            required={isPowerFactorDisplayed}
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.powerFactor"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "powerFactor",
                trimmedValue
              );
            }}
          />
        </Grid.Col>
      </Grid>}
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol} className="get-profile">
          <FileInput
            {...getFieldAttributes("field-5")}
            data-id="field-5"
            label={
              <>
                <div>
                  {t("uploadElectricLoadFile")}
                  <span style={{ color: "#fa5252" }}> *</span>
                </div>
                <span
                  style={{
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    color: "#42b4e6",
                    cursor: "pointer",
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleFileDownload("load");
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFileDownload("load");
                  }}
                >
                  {t("downloadTemplate")}
                </span>
              </>
            }
            accept=".xls, .xlsx, .csv"
            {...form.getInputProps(
              "componentAttributes.loadParameters.profileDetail.loadFile"
            )}
            onChange={(file) => {
              updateLoadState(
                "componentAttributes.loadParameters",
                "profileFile",
                []
              );
              handleFileInputChange(file, "loadFile");
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "isLoadFileClicked",
                !!file
              );
              form.validateField("componentAttributes.loadParameters.profileDetail.powerFactor");
            }}
            rightSection={
              <div style={grayIconStyle}>
                <IconFileSearch size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            disabled={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.useThermalLoad
            }
          />
        </Grid.Col>
        <Grid.Col
          {...responsiveCol}
          className={
            form.errors[
              "componentAttributes.loadParameters.profileDetail.loadFile"
            ]
              ? "get-profile-error"
              : "get-profile-button"
          }
        >
          <Button
            variant="filled"
            onClick={() => {
              handleFileClick();
              updateLoadState(
                "componentAttributes.loadParameters.profileDetail",
                "isLoadFileClicked",
                false
              );
            }}
            sx={{
              backgroundColor: "#626469",
              "&:hover": {
                backgroundColor: "#494b50",
              },
            }}
            disabled={
              loadParameters?.componentAttributes?.loadParameters?.profileDetail
                ?.useThermalLoad ||
              !loadParameters?.componentAttributes?.loadParameters
                ?.profileDetail?.isLoadFileClicked || form.errors["componentAttributes.loadParameters.profileDetail.powerFactor"]
            }
          >
            {t("getProfile")}
          </Button>
        </Grid.Col>
      </Grid>
      {isElectricalDataPresent && (
        <>
          <Grid>
            <Grid.Col {...responsiveCol}>
              <Switch
                label={t("thermalLoad")}
                color={"seGreen.0"}
                labelPosition="left"
                disabled={!dataAssetElectric?.length > 0}
                checked={
                  loadParameters?.componentAttributes?.loadParameters
                    ?.profileDetail?.useThermalLoad
                }
                 {...form.getInputProps(
                    "componentAttributes.loadParameters.profileDetail.useThermalLoad"
                  )}
                data-id="field-thermal-switch"
                onChange={(event) => {
                  updateLoadState(
                    "componentAttributes.loadParameters.profileDetail",
                    "useThermalLoad",
                    event.currentTarget.checked
                  );
                }}
              />
            </Grid.Col>
          </Grid>
          {loadParameters?.componentAttributes?.loadParameters?.profileDetail
            ?.useThermalLoad && (
              <>
            <Grid gutter="md" className="mt-2 mb-2">
                <Grid.Col {...responsiveCol}>
                  <ProjectNumberInput
                    onClick={e => e.stopPropagation()}
                    {...getFieldAttributes("field-8")}
                    data-id="field-8"
                    label={t("loadAttributes.thermalEnergyPrice")}
                    required
                    {...form.getInputProps(
                      "componentAttributes.loadParameters.profileDetail.ThermalEnergyPrice"
                    )}
                    onChange={(e) => {
                      const trimmedValue = formatInputValue(e.currentTarget.value);
                      updateLoadState(
                        "componentAttributes.loadParameters.profileDetail",
                        "ThermalEnergyPrice",
                        trimmedValue
                      );
                    }}
                    rightSection={
                      <div style={{...grayIconStyle, marginRight: "2px"}}>
                        {projectDetails?.projectDTO?.currencyCode || "$"}/kWh
                      </div>
                    }
                    rightSectionProps={{ style: { pointerEvents: "none" } }}
                    rightSectionWidth={60}
                  />
                </Grid.Col>
                <Grid.Col {...responsiveCol}>
                    <ProjectNumberInput
                      onClick={e => e.stopPropagation()}
                      {...getFieldAttributes("field-9")}
                      data-id="field-9"
                      label={t("loadAttributes.thermalEmissionFactor")}
                      required
                      {...form.getInputProps(
                        "componentAttributes.loadParameters.profileDetail.ThermalEmissionFactor"
                      )}
                      onChange={(e) => {
                        const trimmedValue = formatInputValue(e.currentTarget.value);
                        updateLoadState(
                          "componentAttributes.loadParameters.profileDetail",
                          "ThermalEmissionFactor",
                          trimmedValue
                        );
                      }}
                       rightSection={
                        <div style={{ ...grayIconStyle , marginRight: "1.1rem" }}>
                          gCO2/kWh
                        </div>
                      }
                      rightSectionProps={{ style: { pointerEvents: "none" } }}
                      rightSectionWidth={60}
                    />
                </Grid.Col>
              </Grid>

            <Grid gutter="md" className="mt-2 mb-2">
              <Grid.Col {...responsiveCol} className="get-profile">
                <FileInput
                  {...getFieldAttributes("field-7")}
                  data-id="field-7"
                  label={
                    <>
                      <div>
                        {t("uploadThermalLoadFile")}
                        <span style={{ color: "#fa5252" }}> *</span>
                      </div>
                      <span
                        style={{
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                          color: "#42b4e6",
                          cursor: "pointer",
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleFileDownload("load");
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleFileDownload("load");
                        }}
                      >
                        {t("downloadTemplate")}
                      </span>
                    </>
                  }
                  accept=".xls,.xlsx,.csv"
                  {...form.getInputProps(
                    "componentAttributes.loadParameters.profileDetail.thermalFile"
                  )}
                  onChange={(file) => {
                    handleFileInputChange(file, "thermalFile");
                    updateLoadState(
                      "componentAttributes.loadParameters.profileDetail",
                      "isThermalFileClicked",
                      !!file
                    );
                  }}
                  rightSection={
                    <div style={grayIconStyle}>
                      <IconFileSearch size={18} />
                    </div>
                  }
                  rightSectionProps={{ style: { pointerEvents: "none" } }}
                />
              </Grid.Col>
              <Grid.Col
                {...responsiveCol}
                className={
                  form.errors[
                    "componentAttributes.loadParameters.profileDetail.thermalFile"
                  ]
                    ? "get-profile-error"
                    : "get-profile-button"
                }
              >
                <Button
                  variant="filled"
                  onClick={() => {
                    handleFileClick();
                    updateLoadState(
                      "componentAttributes.loadParameters.profileDetail",
                      "isThermalFileClicked",
                      false
                    );
                  }}
                  disabled={
                    !loadParameters?.componentAttributes?.loadParameters
                      ?.profileDetail?.isThermalFileClicked
                  }
                  sx={{
                    backgroundColor: "#626469",
                    "&:hover": {
                      backgroundColor: "#494b50",
                    },
                  }}
                >
                  {t("getProfile")}
                </Button>
              </Grid.Col>
            </Grid>
            </>
          )}
        </>
      )}
      <Grid gutter="md" className="mb-2">
        <Grid.Col
          gutter="md"
          className="mt-2 mb-2"
          id="timeSeries"
          style={{
            position: "relative",
            maxWidth: "100% !important",
            boxSizing: "border-box",
          }}
        >
          <div
            id="scroll-into-graph"
            ref={graphRef}
            data-intro={t('loadAttributes.viewTimeSeriesGraph')}
            data-position="top"
          >
            {((!isThermal && dataAssetElectric?.length > 0) ||
              (isThermal && dataAssetElectric?.length > 0) ||
              (isThermal && dataAssetThermal?.length > 0)) &&
              (chartDelayReady ? (
                <>
                  {/* {collapseSideMenu?.rightMenu && (
                    <se-loading loading={collapseSideMenu?.rightMenu} />
                  )} */}
                  <TimeSeriesGraph
                    dataAssset={memoDataAsssetElectric}
                    dataAssset2={memoDataAsssetThermal}
                    color={"#36a2eb"}
                    name={memoName}
                    chartStartDate={memoChartStartDate}
                    interval={memoInterval}
                    yAxisName={memoYAxisName}
                    // dataAssset={dataAssetElectric}
                    // dataAssset2={isThermal ? dataAssetThermal : []}
                    // color={"#36a2eb"}
                    // name={
                    //   isThermal && dataAssetThermal?.length > 0
                    //     ? "Thermal"
                    //     : "Electrical"
                    // }
                    // chartStartDate={startDate}
                    // interval={
                    //   loadParameters?.componentAttributes?.loadParameters
                    //     ?.profileDetail?.units
                    // }
                    // yAxisName={
                    //   loadParameters?.componentAttributes?.loadParameters
                    //     ?.profileDetail?.units
                    // }
                    embedHeight={500}
                    showMinimap={true}
                    minimapGap={40}
                    lazyLoad={true}
                    startTime={loadParameters?.componentAttributes?.loadParameters
                      ?.profileDetail?.startTime}
                                    />
                                    
                </>
              ) : (
                <div
                  style={{
                    padding: "32px 0",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <se-loading loading={true}></se-loading>
                </div>
              ))}
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
};
export default LoadAttributesForm;
